import type { ReactElement } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { tv } from 'tailwind-variants'
import { P, match } from 'ts-pattern'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import {
  processStepContentElementText,
  resolveInterpolationValues,
} from '@flows-ui/utils/processStepContentElementText'
import type { AnyNullableCustomComponent } from '@flows/core/types/CustomComponent'
import type { AnyCallbackConfig } from '@flows/core/types/dispatchedHookTypes'
import type {
  ListItem as ListItemProps,
  List as ListProps,
} from '@flows/core/types/stepContentTypes'
import type { AnySimpleOnSubmitHook, FlowValues } from '@flows/core/types/stepTypes'
import LineList from '@ui-kit/components/listComponents/LineList'
import type { DataItem, LineListItem } from '@ui-kit/components/listComponents/LineList/types'
import { bgColor, borderColor } from '@ui-kit/styles/colors'
import { paddingLeft as paddingLeftStyle, padding as paddingStyle } from '@ui-kit/styles/padding'

type Props<
  TCustomComponent extends AnyNullableCustomComponent,
  TCallbackConfig extends AnyCallbackConfig,
  TSimpleOnSubmitHook extends AnySimpleOnSubmitHook,
> = Omit<
  ListProps<AnyNullableCustomComponent, AnyCallbackConfig, AnySimpleOnSubmitHook>,
  'data' | 'summaryData'
> & {
  data: (Omit<
    ListItemProps<TCustomComponent, TCallbackConfig, TSimpleOnSubmitHook>,
    'infoButton'
  > & {
    infoButton?: ReactElement
  })[]
  summaryData?: (Omit<
    ListItemProps<TCustomComponent, TCallbackConfig, TSimpleOnSubmitHook>,
    'infoButton'
  > & {
    infoButton?: ReactElement
  })[]
}

const list = tv({
  slots: {
    container: 'flex flex-col',
  },
  variants: {
    border: {
      true: { container: 'mt-5 rounded-lg border' },
    },
  },
})

export const List = <TProps extends Props<any, any, any>>(
  props: TProps & { flowValues: FlowValues }
) => {
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const translateFunction = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })
  const data = props.data.map((element, index): DataItem => {
    const value = match(element)
      .with(
        { label: { values: P.not(P.nullish) } },
        (el) => resolveInterpolationValues(el.label.values, props.flowValues)[el.value]
      )
      .with({ textValue: P.not(P.nullish) }, ({ textValue }) => translateFunction(textValue))
      .otherwise(({ value: innerValue }) => innerValue)

    return {
      ...element,
      key: String(index),
      label: translateFunction(element.label),
      component: element.infoButton,
      value,
    }
  })
  const summaryData = props.summaryData?.map(
    (element, index): LineListItem => ({
      ...element,
      key: String(index),
      label: translateFunction(element.label),
      component: element.infoButton,
    })
  )
  const { container } = list({ border: Boolean(props.borderColor) })
  const hasBorder = Boolean(props.borderColor)
  const spacing = [
    paddingStyle({ space: props.padding ?? (hasBorder ? 'small' : 'xsmall') }),
    paddingLeftStyle({ space: props.paddingLeft ?? 'small' }),
  ]

  return (
    <View
      className={container({
        className: [
          ...spacing,
          bgColor({ color: props.bgColor }),
          borderColor({ color: props.borderColor }),
        ].join(' '),
      })}
    >
      <LineList
        listLabel={props.text ? translateFunction(props.text) : undefined}
        data={data}
        summaryData={summaryData}
        isBoldLabel={props.isBoldLabel}
        gapY={props.gapY}
      />
    </View>
  )
}
